<template>
  <div class="mb-2 overflow-hidden rounded-lg border border-gray-200 bg-white" data-testid="qc-checklist">
    <button
      type="button"
      class="flex w-full items-center gap-2 bg-gray-100 px-4 py-2.5 text-left text-sm font-medium text-gray-800"
      :aria-expanded="open"
      data-testid="qc-checklist-toggle"
      @click="open = !open"
    >
      <span class="pi pi-chevron-right text-[0.7rem] text-gray-500 transition-transform" :class="{ 'rotate-90': open }" aria-hidden="true"></span>
      <span class="pi pi-list text-sm" aria-hidden="true"></span>
      {{ $t('messages.evaluation.qualityCheck.checklist.title') }}
      <span class="ml-auto text-xs font-normal text-gray-400" data-testid="qc-checklist-count">
        {{ hasStates
          ? $t('messages.evaluation.qualityCheck.checklist.tailFull', { total: items.length, found: foundCount, clean: cleanCount, unchecked: uncheckedCount })
          : $t('messages.evaluation.qualityCheck.checklist.tail', { total: checksTotal, found: foundCount, clean: cleanCount }) }}
      </span>
    </button>

    <div v-if="open" class="px-4 py-3">
      <!--
        The items are NOT written here and never may be: their wording lives in
        the database (`report_check_items`, RED-341) and Toomas edits it without
        a release. Render exactly what the server supplies. Since RED-341 that
        is the whole list with a state per item; before it, only the items a
        finding cited — and then the panel says the rest arrive later.
      -->
      <QcNotice icon="pi-info-circle" class="mb-2.5">
        {{ $t('messages.evaluation.qualityCheck.checklist.blocks') }}
        <template v-if="hasStates">
          {{ $t('messages.evaluation.qualityCheck.checklist.introFull', { total: items.length }) }}
          <template v-if="uncheckedCount > 0">{{ $t('messages.evaluation.qualityCheck.checklist.notCheckedNote') }}</template>
        </template>
        <template v-else>{{ $t('messages.evaluation.qualityCheck.checklist.intro', { total: checksTotal }) }}</template>
        <!-- RED-373: why the per-item numbers add up to more than the header. -->
        <template v-if="overlaps"> {{ $t('messages.evaluation.qualityCheck.checklist.overlapNote') }}</template>
      </QcNotice>

      <p v-if="items.length === 0" class="text-sm text-gray-500" data-testid="qc-checklist-unavailable">
        {{ $t('messages.evaluation.qualityCheck.checklist.unavailable', { total: checksTotal }) }}
      </p>

      <ul v-else class="border-t border-gray-100">
        <li
          v-for="item in items"
          :key="item.no"
          class="flex items-start gap-2 border-b border-gray-100 px-0.5 py-2 text-sm leading-snug last:border-b-0"
          :data-block="item.block"
          :data-state="item.state"
          data-testid="qc-checklist-item"
        >
          <span class="w-5 shrink-0 pt-px text-xs tabular-nums text-gray-400">{{ item.no }}.</span>
          <!-- A row with findings is a link to the first one; a jump always
               beats a severity filter (AC 30) — the parent drops it first. -->
          <button
            v-if="item.firstFindingId"
            type="button"
            class="min-w-0 flex-1 text-left text-blue-600 underline underline-offset-2 hover:text-blue-800"
            v-tooltip.top="item.full"
            data-testid="qc-checklist-row"
            @click="emit('jump', item.firstFindingId)"
          >{{ item.short }}</button>
          <span v-else class="min-w-0 flex-1 text-gray-400" v-tooltip.top="item.full" data-testid="qc-checklist-row">{{ item.short }}</span>
          <!-- Clean items are SHOWN, not hidden (§17.2): it is the only place
               the appraiser learns what the machine did NOT flag. An item the
               run did not check is never shown as clean (RED-341). -->
          <span
            v-if="item.findingCount > 0"
            class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-semibold text-amber-500"
            data-testid="qc-checklist-verdict-found"
          >
            <span class="pi pi-exclamation-triangle text-[0.625rem]" aria-hidden="true"></span>
            {{ $t('messages.evaluation.qualityCheck.checklist.found', { n: item.findingCount }) }}
          </span>
          <span
            v-else-if="item.state === 'not_checked'"
            class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-medium text-gray-500"
            data-testid="qc-checklist-verdict-unchecked"
          >
            <span class="pi pi-minus-circle text-[0.625rem]" aria-hidden="true"></span>
            {{ $t('messages.evaluation.qualityCheck.checklist.notChecked') }}
          </span>
          <span
            v-else
            class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-semibold text-emerald-600"
            data-testid="qc-checklist-verdict-clean"
          >
            <span class="pi pi-check text-[0.625rem]" aria-hidden="true"></span>
            {{ $t('messages.evaluation.qualityCheck.checklist.clean') }}
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import QcNotice from './QcNotice.vue'

/**
 * RED-351 / design-spec §17.2 — "Mida masin kontrollis", collapsed under
 * `Lisainfo` below the list (prototype v3; Toomas, RED-340 comment 30633:
 * it must not stand between the appraiser and the findings).
 *
 * Items 1-11 lean on EVS 875; items 12-22 are approver practice with no
 * standard behind them, and `data-block` keeps the two apart.
 */
const props = defineProps({
  /** `[{ no, short, full, block, state }]` as supplied by the server; `state` since RED-341. */
  checklist: { type: Array, default: () => [] },
  findings: { type: Array, default: () => [] },
  checksTotal: { type: Number, default: 22 },
})

const emit = defineEmits(['jump'])

const open = ref(false)

/** Findings grouped by the checklist item number they name. */
const byItem = computed(() => {
  const map = new Map()
  props.findings.forEach(finding => {
    (finding.checklistItems ?? []).forEach(raw => {
      const n = Number.parseInt(String(raw), 10)
      if (!Number.isFinite(n)) {
        return
      }
      if (!map.has(n)) {
        map.set(n, [])
      }
      map.get(n).push(finding)
    })
  })
  return map
})

const items = computed(() =>
  (props.checklist ?? []).map(item => {
    const hits = byItem.value.get(Number(item.no)) ?? []
    return {
      no: item.no,
      short: item.short ?? '',
      full: item.full ?? item.short ?? '',
      block: item.block ?? (Number(item.no) <= 11 ? 'standard' : 'praktika'),
      findingCount: hits.length,
      firstFindingId: hits[0]?.id ?? null,
      // A cited item is found whatever the server said; otherwise trust the
      // server's clean / not_checked. Old payloads carry no state at all.
      state: hits.length > 0 ? 'found' : (item.state ?? 'clean'),
    }
  }),
)

/** The server sent the whole list with a state per item (RED-341). */
const hasStates = computed(() => (props.checklist ?? []).some(item => typeof item.state === 'string'))

/** Distinct checklist items that produced at least one finding. */
const foundCount = computed(() => byItem.value.size)

const uncheckedCount = computed(() => items.value.filter(item => item.state === 'not_checked').length)

/** One finding touches several items: the per-item numbers sum above the findings. */
const overlaps = computed(() => {
  const perItem = items.value.reduce((sum, item) => sum + item.findingCount, 0)
  const distinct = new Set([...byItem.value.values()].flat().map(finding => finding.id)).size
  return perItem > distinct
})

/** Items that were checked and came back clean. Before RED-341: the rest of the total. */
const cleanCount = computed(() => hasStates.value
  ? items.value.filter(item => item.state === 'clean').length
  : Math.max(0, props.checksTotal - foundCount.value))
</script>
